import Storage, {StorageChange, StorageItems, StorageKeys} from "./index.js";

const deserialize = (value: string | null): any => value === null ? null : JSON.parse(value);
const serialize = (value: any): string => JSON.stringify(value);

/**
 * Implements local storage for regular web pages (`window.localStorage`).
 */
export default class WindowLocalStorage extends Storage {
	static KEY_PREFIX = "PowerNotes:";

	constructor() {
		super();

		// Thanks: https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#feature-detecting_localstorage
		function storageAvailable(type: string): boolean {
			let storage;
			try {
				// @ts-ignore
				storage = window[type];
				const x = "__storage_test__";
				storage.setItem(x, x);
				storage.removeItem(x);
				return true;
			} catch (e) {
				return (
					e instanceof DOMException
					&& e.name === "QuotaExceededError"
					// acknowledge QuotaExceededError only if there's something already stored
					&& storage
					&& storage.length !== 0
				);
			}
		}
		if (!storageAvailable("localStorage"))
			throw new Error("Local storage not available");
	}

	async get(keys: StorageKeys | StorageItems): Promise<StorageItems> {
		const getSingleResult = (key: string): ({key: string; value: any} | null) => ({
			key,
			value: deserialize(localStorage.getItem(Storage.addKeyPrefix(key, WindowLocalStorage.KEY_PREFIX)))
		});

		const result: StorageItems = {};

		if (keys === null)
			return result;
		else if (typeof keys === "string") {
			const singleResult = getSingleResult(keys);
			if (singleResult)
				result[singleResult.key] = singleResult.value;
		} else if (Array.isArray(keys)) {
			// keys is an array of strings
			keys.forEach(key => {
				const singleResult = getSingleResult(key);
				if (singleResult)
					result[singleResult.key] = singleResult.value;
			});
		} else {
			/**
			 * keys is a dictionary of {key: defaultValue} entries
			 * See https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/API/storage/StorageArea/get
			 */
			Object.entries(keys).forEach(([key, defaultValue]) => {
				const singleResult = getSingleResult(key);
				if (singleResult)
					result[singleResult.key] = singleResult.value ?? defaultValue;
			});
		}

		return result;
	}

	async set(items: StorageItems) {
		for (const [key, value] of Object.entries(items))
			localStorage.setItem(Storage.addKeyPrefix(key, WindowLocalStorage.KEY_PREFIX), serialize(value));
	}

	async remove(keys: StorageKeys) {
		if (typeof keys === "string")
			localStorage.removeItem(Storage.addKeyPrefix(keys, WindowLocalStorage.KEY_PREFIX));
		else {
			keys?.forEach((key: string) => {
				localStorage.removeItem(Storage.addKeyPrefix(key, WindowLocalStorage.KEY_PREFIX));
			});
		}
	}

	async clear() {
		return localStorage.clear();
	}

	listen(callback: (changes: StorageChange) => void) {
		window.addEventListener("storage", (event: StorageEvent) => {
			const {key, newValue, oldValue} = event;
			if (!key)
				return;
			const unprefixed = Storage.removeKeyPrefix(key, WindowLocalStorage.KEY_PREFIX);
			if (!unprefixed)
				return;
			callback({[unprefixed]: {newValue: deserialize(newValue), oldValue: deserialize(oldValue)}});
		});
	}
}
